/**
 * Theme Switcher with Server-Side Persistence
 * Handles dark/light theme toggle with both localStorage and session storage
 * Prevents flash of wrong theme on page load
 */

(function() {
    'use strict';

    // Theme constants
    const THEME_KEY = 'tglist-theme';
    const DARK_THEME_CLASS = 'dark-theme';
    const LIGHT_THEME = 'light';
    const DARK_THEME = 'dark';

    /**
     * Get current theme from localStorage or system preference
     */
    function getCurrentTheme() {
        const savedTheme = localStorage.getItem(THEME_KEY);
        
        if (savedTheme) {
            return savedTheme;
        }
        
        // Check system preference
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return DARK_THEME;
        }
        
        return LIGHT_THEME;
    }

    /**
     * Apply theme to document
     */
    function applyTheme(theme) {
        // Safety check - ensure body exists
        if (!document.body) {
            // If body doesn't exist yet, wait for it
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', function() {
                    applyTheme(theme);
                });
                return;
            }
            return;
        }
        
        if (theme === DARK_THEME) {
            document.body.classList.add(DARK_THEME_CLASS);
            document.documentElement.classList.add(DARK_THEME_CLASS);
        } else {
            document.body.classList.remove(DARK_THEME_CLASS);
            document.documentElement.classList.remove(DARK_THEME_CLASS);
        }
        
        // Update toggle button icon if it exists
        updateToggleIcon(theme);
    }

    /**
     * Update toggle button icon
     */
    function updateToggleIcon(theme) {
        const toggleButtons = document.querySelectorAll('[data-theme-toggle]');
        if (!toggleButtons.length) return;

        toggleButtons.forEach(function(toggleBtn) {
            const sunIcon = toggleBtn.querySelector('.theme-icon-sun');
            const moonIcon = toggleBtn.querySelector('.theme-icon-moon');

            if (theme === DARK_THEME) {
                if (sunIcon) sunIcon.style.display = 'inline-block';
                if (moonIcon) moonIcon.style.display = 'none';
                toggleBtn.setAttribute('aria-label', 'Перемкнути на світлу тему');
            } else {
                if (sunIcon) sunIcon.style.display = 'none';
                if (moonIcon) moonIcon.style.display = 'inline-block';
                toggleBtn.setAttribute('aria-label', 'Перемкнути на темну тему');
            }
        });
    }

    /**
     * Get locale prefix from URL
     */
    function getLocalePrefix() {
        const pathParts = window.location.pathname.split('/').filter(part => part.length > 0);
        if (pathParts[0] && pathParts[0].length === 2 && /^[a-z]{2}$/.test(pathParts[0])) {
            return `/${pathParts[0]}`;
        }
        return '';
    }

    /**
     * Save theme to server session
     */
    function saveThemeToServer(theme) {
        // Send theme to server asynchronously
        const url = getLocalePrefix() + '/theme/set';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
            },
            body: JSON.stringify({ theme: theme })
        }).catch(function(error) {
            console.warn('Failed to save theme to server:', error);
        });
    }

    /**
     * Toggle theme
     */
    function toggleTheme() {
        const currentTheme = getCurrentTheme();
        const newTheme = currentTheme === DARK_THEME ? LIGHT_THEME : DARK_THEME;
        
        // Save to localStorage (instant)
        localStorage.setItem(THEME_KEY, newTheme);
        
        // Save to server session (async)
        saveThemeToServer(newTheme);
        
        // Apply theme
        applyTheme(newTheme);
    }

    /**
     * Initialize theme on page load
     */
    function initTheme() {
        // Only initialize if body exists
        if (!document.body) return;
        
        const currentTheme = getCurrentTheme();
        applyTheme(currentTheme);
        
        // Sync with server if theme exists in localStorage
        if (localStorage.getItem(THEME_KEY)) {
            saveThemeToServer(currentTheme);
        }
    }

    /**
     * Setup event listeners
     */
    function setupEventListeners() {
        const toggleButtons = document.querySelectorAll('[data-theme-toggle]');
        if (!toggleButtons.length) {
            return;
        }

        toggleButtons.forEach(function(toggleBtn) {
            toggleBtn.addEventListener('click', function(e) {
                e.preventDefault();
                toggleTheme();
            });
        });
    }

    // Wait for body to exist before initializing
    function init() {
        if (document.body) {
            initTheme();
            setupEventListeners();
        } else {
            // If body doesn't exist, wait for DOMContentLoaded
            document.addEventListener('DOMContentLoaded', function() {
                initTheme();
                setupEventListeners();
            });
        }
    }

    // Initialize
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Listen for system theme changes
    if (window.matchMedia) {
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', function(e) {
            // Only auto-switch if user hasn't manually set a preference
            if (!localStorage.getItem(THEME_KEY)) {
                const newTheme = e.matches ? DARK_THEME : LIGHT_THEME;
                applyTheme(newTheme);
                saveThemeToServer(newTheme);
            }
        });
    }

    // Expose toggle function globally for inline onclick handlers if needed
    window.toggleTheme = toggleTheme;
})();
